import moment from 'moment';

export const getVisibleDateKeys = (state) => {
  const data = state?.calendar?.data;
  if (!data) return [];

  const {
    viewType,
    selectedDate,
    selectedWeekViewDayToggle,
    selectedDaysOfWeekToShowInCurrentWeek,
    selectedNumberOfDaysToShowInWeek,
    currentlyDisplayedWeekdays,
    calendarItems,
    beforeRecurringWorkouts,
    afterRecurringWorkouts,
  } = data;

  if (viewType === 'dayView') {
    return [moment(selectedDate).format('YYYY-MM-DD')];
  }

  if (viewType === 'monthView') {
    const gridStart = moment(selectedDate).startOf('month').startOf('week');
    const gridEnd = moment(selectedDate).endOf('month').endOf('week');
    const totalDays = gridEnd.diff(gridStart, 'days') + 1;
    const dates = [];
    for (let i = 0; i < totalDays; i += 1) {
      dates.push(gridStart.clone().add(i, 'days').format('YYYY-MM-DD'));
    }
    return dates;
  }

  if (viewType === 'weekView') {
    if (selectedWeekViewDayToggle && selectedWeekViewDayToggle !== 'Everyday') {
      return Array.isArray(currentlyDisplayedWeekdays)
        ? currentlyDisplayedWeekdays.map((d) => d.date)
        : [];
    }

    const daysToInclude = Array.isArray(selectedDaysOfWeekToShowInCurrentWeek)
      ? selectedDaysOfWeekToShowInCurrentWeek
      : [];
    const daysToShowMax = selectedNumberOfDaysToShowInWeek || 7;
    const includeEmptyDays = daysToInclude.includes('Empty Days');

    const dates = [];
    for (let i = 0; i < 7; i += 1) {
      const current = moment(selectedDate).clone().add(i, 'days');
      const dayName = current.format('dddd');
      const formattedDate = current.format('YYYY-MM-DD');
      const athleteAddedItems = calendarItems?.[formattedDate]?.athleteAddedItems;

      const isDateEmpty = (!calendarItems?.[formattedDate]?.exercises
        || calendarItems[formattedDate].exercises.length === 0)
        && !beforeRecurringWorkouts?.[formattedDate]?.length
        && !afterRecurringWorkouts?.[formattedDate]?.length
        && !athleteAddedItems?.length;

      const dayMatchesFilter = daysToInclude.length === 0
        || daysToInclude.includes('Everyday')
        || daysToInclude.includes(dayName);

      if (dayMatchesFilter && (!isDateEmpty || includeEmptyDays)) {
        dates.push(formattedDate);
        if (dates.length >= daysToShowMax) break;
      }
    }
    return dates;
  }

  return [];
};

export const getVisibleProgramDayNumbers = (state) => {
  const data = state?.calendar?.data;
  if (!data) return [];

  const {
    viewType,
    selectedProgramDayNumber,
    nonDateBasedProgramItems,
    groupByFilter,
  } = data;

  if (!nonDateBasedProgramItems) return [];

  if (viewType === 'dayView') {
    return selectedProgramDayNumber != null ? [selectedProgramDayNumber] : [];
  }

  if (viewType === 'weekView') {
    const selectedItem = nonDateBasedProgramItems[selectedProgramDayNumber];
    if (!selectedItem) return [];

    const items = Object.values(nonDateBasedProgramItems);

    if (groupByFilter === 'Day') {
      return items
        .filter((item) => item?.dayInWeek === selectedItem.dayInWeek)
        .map((item) => item.dayNumber ?? item.programDayNumber)
        .filter((n) => n !== undefined && n !== null);
    }

    return items
      .filter((item) => item?.weekNumber === selectedItem.weekNumber)
      .map((item) => item.dayNumber ?? item.programDayNumber)
      .filter((n) => n !== undefined && n !== null);
  }

  return [];
};

export const hasPreSaveInVisibleRange = (state) => {
  const data = state?.calendar?.data;
  if (!data) return false;

  if (data.programType === 'free-form-program') {
    const items = data.nonDateBasedProgramItems;
    if (!items) return false;
    const dayNumbers = getVisibleProgramDayNumbers(state);
    return dayNumbers.some((dn) => {
      const exercises = items[dn]?.exercises;
      return Array.isArray(exercises) && exercises.some((e) => e?.preSave === true);
    });
  }

  const { calendarItems } = data;
  if (!calendarItems) return false;
  const dates = getVisibleDateKeys(state);
  return dates.some((d) => {
    const exercises = calendarItems[d]?.exercises;
    return Array.isArray(exercises) && exercises.some((e) => e?.preSave === true);
  });
};
